import { useState } from "react";
import { Link, useNavigate, useParams } from "react-router-dom";
import {
  AlertTriangle,
  ArrowLeft,
  Ban,
  CheckCircle2,
  Eye,
  Hourglass,
  Mail,
  MinusCircle,
  Pencil,
  Repeat,
  RotateCcw,
  Send,
  Trash2,
} from "lucide-react";
import { AccusesReception, type ProblemePropose } from "@/composants/accuses-reception";
import {
  ChampMotif,
  FormulaireAnnulationReliquat,
  FormulaireCommandeAilleurs,
  quantitesEnLettres,
} from "@/composants/gestes-ligne-fournisseur";
import { EcartsPrixDeLaCommande } from "@/composants/ecarts-prix";
import { FrancoDeLaCommande } from "@/composants/franco";
import { DemandesAvoirDeLaCommande } from "@/composants/demandes-avoir";
import { EnvoiParCourriel, TentativesDEnvoi } from "@/composants/envoi-courriel";
import { FraisDeLaCommande } from "@/composants/frais-fournisseur";
import { ReceptionsDeLaCommande } from "@/composants/receptions-logistiques";
import { ChampsLigne, champsDeLigne, valeursDeLigne } from "@/composants/ligne-fournisseur";
import { Bouton } from "@/composants/ui/bouton";
import { Carte, CorpsCarte, EnTeteCarte } from "@/composants/ui/carte";
import { Champ, ChampPuces, ChampSelection, ChampTexte } from "@/composants/ui/champ";
import { Chargement, Erreur, Vide } from "@/composants/ui/etats";
import { Donnee, Etiquette } from "@/composants/ui/etiquette";
import { messageDe, useChargement } from "@/lib/chargement";
import {
  DECISIONS_NON_CONFORMITE,
  dateSemaine,
  decisionNonConformite,
  euros,
  horodatage,
  quantiteLisible,
  statutCommandeFournisseur,
  tonStatutCommandeFournisseur,
  TYPES_PROBLEME_FOURNISSEUR,
  typeProblemeFournisseur,
} from "@/lib/libelles";
import {
  api,
  type CommandeClientProposee,
  type FicheCommandeFournisseur,
  type LigneCommandeFournisseur,
  type LigneDeLaFicheFournisseur,
  type LigneLiee,
  type Remplacement,
  type DecisionNonConformite,
  type ProblemeFournisseur,
  type TypeProblemeFournisseur,
} from "@/lib/api";
import { useSession } from "@/session";

/**
 * La fiche d'une commande fournisseur (LOT 2, ticket #14) : son en-tête, ses
 * lignes — chacune pour un lot, ou pour aucun (ADR-0001) —, ses AR et leurs
 * écarts (ticket #17), ses problèmes (ticket #16), et les gestes du chargé
 * d'affaires. LABGEST ne l'envoie pas : on la passe chez le fournisseur,
 * puis on la marque envoyée.
 *
 * La permission dit le geste, `intervenable` les commandes client servies —
 * toutes, pour la commande ; la sienne, pour une ligne —, `gestes`,
 * `corrigeable` et `ajout_ligne` l'avancement : l'écran ne recopie aucune règle,
 * il les lit. Sans la permission de voir les montants, aucun prix n'est montré.
 *
 * Envoyée, une ligne ne se corrige plus (ticket #38, ADR-0045) : on en annule
 * le reliquat, on la commande ailleurs, on ajoute une ligne avec son motif.
 *
 * Une commande sert les commandes client d'un seul propriétaire ; ses lignes se
 * lisent groupées par commande client (ticket #37). Après un transfert, celles de
 * l'autre propriétaire ne se lisent qu'en nombre.
 */
export function PageCommandeFournisseur() {
  const { id = "" } = useParams();
  const session = useSession();
  const aLaPermission = session.peut("GERER_COMMANDE_FOURNISSEUR");
  const voitLesMontants = session.peut("CONSULTER_MONTANTS");

  const { donnees, erreur, enCours, recharger } = useChargement<FicheCommandeFournisseur>(
    (signal) => api.commandeFournisseur(id, signal),
    [id],
  );
  const [modifiee, setModifiee] = useState<FicheCommandeFournisseur | null>(null);
  const [correction, setCorrection] = useState(false);
  const [annulation, setAnnulation] = useState(false);
  const [courriel, setCourriel] = useState(false);
  const [motifAnnulation, setMotifAnnulation] = useState("");
  const [refus, setRefus] = useState<string | null>(null);
  // Un écart d'AR propose un problème : la carte des problèmes l'ouvre prérempli.
  const [proposition, setProposition] = useState<ProblemePropose | null>(null);

  if (erreur) return <Erreur message={erreur} onReessayer={recharger} />;
  if (!donnees || enCours) return <Chargement message="Chargement de la commande fournisseur…" />;

  const fiche = modifiee && modifiee.id === id ? modifiee : donnees;
  const peutAgir = aLaPermission && fiche.intervenable;
  const [premiere] = fiche.commandes_clients;
  const numeros = fiche.commandes_clients.map((servie) => servie.numero_commande_client);
  // Elle sert aussi une commande client sur laquelle on n'agit pas : on écrit
  // ses lignes, pas la commande entière.
  const enPartie = !fiche.intervenable && fiche.lignes.some((ligne) => ligne.intervenable);
  const peutCorriger = peutAgir && fiche.en_tete_corrigeable;
  const peutEnvoyer = peutAgir && fiche.gestes.includes("ENVOI");
  // Le message est parti mais la marque a échoué : renvoyer ferait un doublon, « Marquer
  // envoyée » rattrape la commande.
  const departSansMarque = fiche.envois_courriel.some((e) => e.reussi && !e.marquee_envoyee);
  // Ce qui est reçu ne s'annule pas : l'annulation porte alors sur le reliquat.
  const aRecu = fiche.annulation_motivee;
  const lignesAnnulees = fiche.lignes.filter((ligne) => ligne.quantite_annulee > 0).length;
  const agir = async (geste: () => Promise<FicheCommandeFournisseur>) => {
    setRefus(null);
    try {
      setModifiee(await geste());
      return true;
    } catch (cause) {
      setRefus(messageDe(cause));
      return false;
    }
  };

  return (
    <div className="flex flex-col gap-5">
      {premiere ? (
        <Link
          to={`/commandes/${premiere.id}`}
          className="inline-flex items-center gap-1.5 text-sm text-encre-douce hover:text-encre"
        >
          <ArrowLeft className="h-4 w-4" aria-hidden />
          La commande client {premiere.numero_commande_client}
        </Link>
      ) : null}

      <div className="flex flex-wrap items-start justify-between gap-3">
        <div className="min-w-0">
          <h1 className="break-words text-xl font-semibold text-encre">
            Commande chez {fiche.fournisseur.nom}
          </h1>
          <p className="mt-1 text-sm text-encre-douce">
            {numeros.length > 1 ? "Pour les commandes client " : "Pour la commande client "}
            <span className="font-mono">{numeros.join(", ")}</span>
            {fiche.autres_commandes_clients.length > 0
              ? `, et ${autresCommandes(fiche.autres_commandes_clients.length)}`
              : null}
          </p>
        </div>
        <Etiquette ton={tonStatutCommandeFournisseur(fiche.statut)}>
          {statutCommandeFournisseur(fiche.statut)}
        </Etiquette>
      </div>

      {fiche.intervenable ? null : (
        <p
          role="status"
          className="flex items-start gap-2 rounded-[6px] border border-filet bg-chene-clair/25 px-4 py-3 text-sm text-encre-douce"
        >
          <Eye className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
          <span>
            {enPartie
              ? "Cette commande sert aussi une commande client sur laquelle vous n'intervenez pas : vous écrivez vos lignes, pas la commande entière."
              : "Vous consultez cette commande sans pouvoir y intervenir."}
          </span>
        </p>
      )}

      {refus ? <Erreur message={refus} /> : null}

      {peutAgir && fiche.gestes.length > 0 ? (
        <div className="flex flex-wrap gap-2">
          {peutEnvoyer && !courriel && !departSansMarque ? (
            <Bouton variante="principal" onClick={() => setCourriel(true)}>
              <Mail className="h-4 w-4" aria-hidden />
              Envoyer par courriel
            </Bouton>
          ) : null}
          {peutEnvoyer ? (
            <Bouton
              variante={departSansMarque ? "principal" : "secondaire"}
              onClick={() => agir(() => api.envoyerCommandeFournisseur(fiche.id))}
            >
              <Send className="h-4 w-4" aria-hidden />
              Marquer envoyée
            </Bouton>
          ) : null}
          {fiche.gestes.includes("ANNULATION") && !annulation ? (
            <Bouton onClick={() => setAnnulation(true)}>
              <Ban className="h-4 w-4" aria-hidden />
              {aRecu ? "Annuler le reliquat de la commande" : "Annuler la commande"}
            </Bouton>
          ) : null}
        </div>
      ) : null}

      {courriel && peutEnvoyer && !departSansMarque ? (
        <EnvoiParCourriel
          fournisseur={fiche.fournisseur}
          dernierEchec={fiche.envois_courriel[0]?.reussi === false}
          envoyer={async (contactId) => {
            const parti = await agir(() =>
              api.envoyerCommandeFournisseurParCourriel(fiche.id, contactId),
            );
            // L'échec est écrit au journal : la fiche relue en porte l'erreur.
            if (!parti) setModifiee(await api.commandeFournisseur(fiche.id));
            return parti;
          }}
        />
      ) : null}
      {fiche.intervenable ? <TentativesDEnvoi envois={fiche.envois_courriel} /> : null}

      {annulation ? (
        <form
          aria-label="Annuler la commande"
          className="flex flex-col gap-3 rounded-[6px] border border-filet px-4 py-3"
          onSubmit={async (evenement) => {
            evenement.preventDefault();
            await agir(() =>
              api.annulerCommandeFournisseur(fiche.id, motifAnnulation.trim() || undefined),
            );
            setAnnulation(false);
          }}
        >
          <p className="text-sm text-encre">
            {aRecu
              ? "Cette commande a déjà reçu : ce qui est arrivé reste, " +
                "le reliquat de chaque ligne s'annule."
              : "La commande entière s'annule."}
          </p>
          <ChampMotif valeur={motifAnnulation} onChange={setMotifAnnulation} requis={aRecu} />
          <div className="flex flex-wrap gap-2">
            <Bouton type="submit" disabled={aRecu && !motifAnnulation.trim()}>
              Confirmer l'annulation
            </Bouton>
            <Bouton type="button" variante="discret" onClick={() => setAnnulation(false)}>
              Garder la commande
            </Bouton>
          </div>
        </form>
      ) : null}

      {(fiche.statut === "RECEPTIONNEE" || fiche.statut === "CLOTUREE_PARTIELLE") &&
      lignesAnnulees > 0 ? (
        <p role="status" className="text-sm text-encre">
          {fiche.statut === "CLOTUREE_PARTIELLE"
            ? "Une partie de la commande est arrivée, le reste est annulé. "
            : "Chaque ligne est arrivée ou annulée. "}
          Reliquat annulé sur {lignesAnnulees > 1 ? `${lignesAnnulees} lignes` : "une ligne"} : le
          détail est sur chacune.
        </p>
      ) : null}
      {fiche.annulation_de_la_commande ? (
        <p role="status" className="text-sm text-encre">
          {fiche.annulation_de_la_commande.chemin === "BLOC"
            ? "Annulée d'un bloc : rien n'était arrivé."
            : "Reliquats annulés : rien n'était arrivé, et tout le reliquat l'a été, ligne par ligne."}
          {fiche.annulation_de_la_commande.motifs.length > 0
            ? ` ${fiche.annulation_de_la_commande.motifs.length > 1 ? "Motifs" : "Motif"} : ${fiche.annulation_de_la_commande.motifs.join(" ; ")}.`
            : " Aucun motif n'a été saisi."}
        </p>
      ) : null}

      <Carte>
        <EnTeteCarte className="flex items-center justify-between gap-3">
          <h2 className="text-sm font-semibold text-encre">La commande</h2>
          {peutCorriger ? (
            <Bouton taille="compact" variante="discret" onClick={() => setCorrection((o) => !o)}>
              <Pencil className="h-3.5 w-3.5" aria-hidden />
              {correction ? "Fermer" : "Corriger"}
            </Bouton>
          ) : null}
        </EnTeteCarte>
        <CorpsCarte>
          {correction ? (
            <FormulaireEnTete
              fiche={fiche}
              onAnnuler={() => setCorrection(false)}
              onValider={async (champs) => {
                if (await agir(() => api.corrigerCommandeFournisseur(fiche.id, champs))) {
                  setCorrection(false);
                }
              }}
            />
          ) : (
            <>
              <dl className="grid grid-cols-2 gap-x-4 gap-y-4 sm:grid-cols-3">
                <Donnee libelle="Fournisseur">
                  <Link
                    to={`/fournisseurs/${fiche.fournisseur.id}`}
                    className="text-encre underline underline-offset-4"
                  >
                    {fiche.fournisseur.nom}
                  </Link>
                </Donnee>
                <Donnee libelle="Numéro">
                  <span className="tabular-nums">{fiche.numero}</span>
                </Donnee>
                <Donnee libelle="Référence chez le fournisseur">
                  {fiche.reference_fournisseur ?? "—"}
                </Donnee>
                <Donnee libelle="Date de commande">{dateSemaine(fiche.date_commande)}</Donnee>
                <Donnee libelle="Date demandée">
                  {fiche.date_demandee ? dateSemaine(fiche.date_demandee) : "Non connue"}
                </Donnee>
                <Donnee libelle="Date confirmée fournisseur">
                  {fiche.date_annoncee ? (
                    <>
                      {dateSemaine(fiche.date_annoncee)}
                      {fiche.ecart_annonce_semaines !== null ? (
                        <span className="block text-sm font-normal text-encre-douce">
                          {ecartEnToutesLettres(fiche.ecart_annonce_semaines)}
                        </span>
                      ) : null}
                    </>
                  ) : (
                    "Aucune date confirmée par un AR"
                  )}
                </Donnee>
                <Donnee libelle="Date prévue">
                  {fiche.date_prevue ? dateSemaine(fiche.date_prevue) : "À saisir"}
                </Donnee>
                <Donnee libelle="Accusé de réception">
                  {fiche.ar_recu_le ? `Reçu le ${dateSemaine(fiche.ar_recu_le)}` : "Pas encore reçu"}
                </Donnee>
                {voitLesMontants ? (
                  <Donnee libelle="Montant HT">
                    <span className="tabular-nums">
                      {fiche.montant_ht === null
                        ? "Non chiffrée : une ligne au moins est sans prix"
                        : euros(fiche.montant_ht)}
                    </span>
                  </Donnee>
                ) : null}
              </dl>
              {fiche.dates_annoncees.length > 0 ? (
                <section aria-label="Dates confirmées, AR par AR" className="mt-4">
                  <h3 className="text-sm font-medium text-encre">Dates confirmées, AR par AR</h3>
                  <ul className="mt-1 flex flex-col gap-1 text-sm text-encre-douce">
                    {fiche.dates_annoncees.map((annonce) => (
                      <li key={annonce.ar_id}>
                        AR du {dateSemaine(annonce.date_ar)} : confirme le{" "}
                        {dateSemaine(annonce.date_annoncee)}
                        {annonce.courante ? " (le dernier, celui que la commande suit)" : ""}
                      </li>
                    ))}
                  </ul>
                </section>
              ) : null}
            </>
          )}
        </CorpsCarte>
      </Carte>

      <Lignes
        fiche={fiche}
        aLaPermission={aLaPermission}
        voitLesMontants={voitLesMontants}
        agir={agir}
      />

      <FrancoDeLaCommande fiche={fiche} voitLesMontants={voitLesMontants} />

      <ReceptionsDeLaCommande fiche={fiche} agir={agir} />

      <FraisDeLaCommande fiche={fiche} peutAgir={peutAgir} agir={agir} />

      <EcartsPrixDeLaCommande fiche={fiche} agir={agir} />

      <DemandesAvoirDeLaCommande fiche={fiche} agir={agir} />

      <AccusesReception
        fiche={fiche}
        peutAgir={peutAgir}
        peutDeposer={session.peut("IMPORTER_DOCUMENT_OCR") && fiche.intervenable}
        voitLesMontants={voitLesMontants}
        agir={agir}
        onProposerProbleme={(propose) => {
          setProposition(propose);
          document.getElementById("problemes-fournisseur")?.scrollIntoView({ block: "start" });
        }}
      />

      <Problemes
        fiche={fiche}
        peutAgir={peutAgir}
        voitLesMontants={voitLesMontants}
        agir={agir}
        proposition={proposition}
        onPropositionTraitee={() => setProposition(null)}
      />
    </div>
  );
}

/** Décisions 103 et 124 : l'écart se lit en semaines ISO et en toutes lettres, jamais par un signe ou une couleur. */
function ecartEnToutesLettres(semaines: number): string {
  if (semaines === 0) return "confirmée la même semaine que la date demandée";
  const n = Math.abs(semaines);
  return `confirmée ${n} semaine${n > 1 ? "s" : ""} ${semaines > 0 ? "après" : "avant"} la date demandée`;
}

function FormulaireEnTete({
  fiche,
  onAnnuler,
  onValider,
}: {
  fiche: FicheCommandeFournisseur;
  onAnnuler: () => void;
  onValider: (champs: Record<string, unknown>) => Promise<void>;
}) {
  const [dateCommande, setDateCommande] = useState(fiche.date_commande);
  const [dateDemandee, setDateDemandee] = useState(fiche.date_demandee ?? "");
  const [datePrevue, setDatePrevue] = useState(fiche.date_prevue ?? "");
  const [reference, setReference] = useState(fiche.reference_fournisseur ?? "");
  // Ticket #58 : envoyée, la correction dit pourquoi ; la base exige un motif neuf.
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const motifManquant = fiche.correction_motivee && !motif.trim();
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        await onValider({
          date_commande: dateCommande,
          date_demandee: dateDemandee || null,
          date_prevue: datePrevue || null,
          reference_fournisseur: reference.trim() || null,
          ...(fiche.correction_motivee ? { motif: motif.trim() } : {}),
        });
        setEnvoi(false);
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2 lg:grid-cols-4">
        <Champ
          libelle="Date de commande"
          type="date"
          required
          value={dateCommande}
          onChange={(e) => setDateCommande(e.target.value)}
        />
        <Champ
          libelle="Date demandée"
          type="date"
          aide="Celle que vous avez demandée au fournisseur : aucun AR ne l'écrase."
          value={dateDemandee}
          onChange={(e) => setDateDemandee(e.target.value)}
        />
        <Champ
          libelle="Date prévue"
          type="date"
          aide="Celle que la commande suit : la dernière confirmée par un AR."
          value={datePrevue}
          onChange={(e) => setDatePrevue(e.target.value)}
        />
        <Champ
          libelle="Référence chez le fournisseur"
          value={reference}
          onChange={(e) => setReference(e.target.value)}
        />
      </div>
      {fiche.correction_motivee ? (
        <Champ
          libelle="Motif de la correction"
          aide="La commande est envoyée : dites pourquoi vous corrigez ses dates ou sa référence."
          required
          value={motif}
          onChange={(e) => setMotif(e.target.value)}
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || motifManquant}>
          Enregistrer
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

type Agir = (geste: () => Promise<FicheCommandeFournisseur>) => Promise<boolean>;

function autresCommandes(nombre: number) {
  return nombre > 1
    ? `${nombre} autres commandes client que vous ne voyez pas`
    : "une autre commande client que vous ne voyez pas";
}

/**
 * Les lignes, groupées par commande client. En préparation, elles se corrigent et
 * se retirent ; envoyée, chacune dit en toutes lettres ce qui est commandé, reçu,
 * annulé et reste à recevoir, ce qu'elle remplace ou ce qui la remplace, et
 * offre ses gestes (ADR-0045). Chaque ligne dit si l'on agit sur sa commande
 * client. Les lignes des commandes client qu'on ne voit pas se comptent, sans
 * rien en dire.
 */
function Lignes({
  fiche,
  aLaPermission,
  voitLesMontants,
  agir,
}: {
  fiche: FicheCommandeFournisseur;
  aLaPermission: boolean;
  voitLesMontants: boolean;
  agir: Agir;
}) {
  const navigate = useNavigate();
  const peutEcrire = aLaPermission && fiche.corrigeable;
  const peutAjouter = aLaPermission && fiche.ajout_ligne !== null;
  // Les commandes client qu'une ligne peut servir, chacune avec ses lots.
  const proposees = useChargement<CommandeClientProposee[] | null>(
    (signal) =>
      peutAjouter
        ? api.commandesProposees({ commande_fournisseur_id: fiche.id }, signal)
        : Promise.resolve(null),
    [fiche.id, peutAjouter],
  );
  const [enCours, setEnCours] = useState<{ ligne: string; geste: GesteOuvert } | null>(null);
  const commandes = proposees.donnees ?? [];
  const [premiere] = commandes;
  const fermer = () => setEnCours(null);
  const ouvert = (ligne: string, geste: GesteOuvert) =>
    enCours?.ligne === ligne && enCours.geste === geste;

  return (
    <Carte>
      <EnTeteCarte>
        <h2 className="text-sm font-semibold text-encre">Lignes</h2>
      </EnTeteCarte>
      {fiche.commandes_clients.map((servie) => (
        <section
          key={servie.id}
          aria-label={`Lignes de la commande client ${servie.numero_commande_client}`}
          className="border-b border-filet last:border-b-0"
        >
          <h3 className="bg-chene-clair/25 px-4 py-2 text-sm font-medium text-encre">
            Commande client{" "}
            <Link
              to={`/commandes/${servie.id}`}
              className="font-mono underline underline-offset-4"
            >
              {servie.numero_commande_client}
            </Link>
          </h3>
          <ul className="divide-y divide-filet">
            {fiche.lignes
              .filter((ligne) => ligne.commande_client_id === servie.id)
              .map((ligne) =>
                ouvert(ligne.id, "CORRECTION") ? (
                  <li key={ligne.id} className="px-4 py-3">
                    <FormulaireLigne
                      ligne={ligne}
                      commandes={commandes}
                      avecPrix={voitLesMontants}
                      libelleValider="Enregistrer la ligne"
                      onAnnuler={fermer}
                      onValider={async (champs) => {
                        const faite = await agir(() =>
                          api.corrigerLigneFournisseur(fiche.id, ligne.id, champs),
                        );
                        if (faite) fermer();
                        return faite;
                      }}
                    />
                  </li>
                ) : (
                  <li key={ligne.id} className="flex flex-col gap-2 px-4 py-3">
                    <div className="flex flex-col gap-2 sm:flex-row sm:items-center sm:gap-4">
                      <div className="min-w-0 flex-1 text-sm">
                        <p className="font-medium text-encre">
                          {ligne.designation}
                          {ligne.reference ? (
                            <span className="font-normal text-encre-douce">
                              {" "}
                              — réf. {ligne.reference}
                            </span>
                          ) : null}
                        </p>
                        <p className="text-encre-douce">
                          {ligne.code_lot
                            ? `Lot ${ligne.code_lot}`
                            : "Sans lot (article générique)"}
                        </p>
                      </div>
                      <span className="text-sm tabular-nums text-encre sm:w-28 sm:shrink-0 sm:text-right">
                        {quantite(ligne)}
                      </span>
                      {voitLesMontants ? (
                        <span className="text-sm tabular-nums text-encre-douce sm:w-40 sm:shrink-0 sm:text-right">
                          {prixDeLaLigne(ligne)}
                        </span>
                      ) : null}
                      {peutEcrire && ligne.intervenable ? (
                        <span className="flex gap-1 sm:shrink-0">
                          <Bouton
                            taille="compact"
                            variante="discret"
                            aria-label={`Corriger ${ligne.designation}`}
                            onClick={() => setEnCours({ ligne: ligne.id, geste: "CORRECTION" })}
                          >
                            <Pencil className="h-3.5 w-3.5" aria-hidden />
                            Corriger
                          </Bouton>
                          <Bouton
                            taille="compact"
                            variante="discret"
                            aria-label={`Retirer ${ligne.designation}`}
                            onClick={() =>
                              agir(() => api.retirerLigneFournisseur(fiche.id, ligne.id))
                            }
                          >
                            <Trash2 className="h-3.5 w-3.5" aria-hidden />
                            Retirer
                          </Bouton>
                        </span>
                      ) : null}
                    </div>
                    <HistoireDeLaLigne ligne={ligne} envoyee={!fiche.corrigeable} />
                    {aLaPermission && ligne.gestes.length > 0 && !enCours ? (
                      <span className="flex flex-wrap gap-1">
                        {ligne.gestes.includes("ANNULATION_RELIQUAT") ? (
                          <Bouton
                            taille="compact"
                            variante="discret"
                            aria-label={`Annuler le reliquat de ${ligne.designation}`}
                            onClick={() => setEnCours({ ligne: ligne.id, geste: "RELIQUAT" })}
                          >
                            <MinusCircle className="h-3.5 w-3.5" aria-hidden />
                            Annuler le reliquat
                          </Bouton>
                        ) : null}
                        {ligne.gestes.includes("COMMANDE_AILLEURS") ? (
                          <Bouton
                            taille="compact"
                            variante="discret"
                            aria-label={`Commander ailleurs ${ligne.designation}`}
                            onClick={() => setEnCours({ ligne: ligne.id, geste: "AILLEURS" })}
                          >
                            <Repeat className="h-3.5 w-3.5" aria-hidden />
                            Commander ailleurs
                          </Bouton>
                        ) : null}
                      </span>
                    ) : null}
                    {ouvert(ligne.id, "RELIQUAT") ? (
                      <FormulaireAnnulationReliquat
                        ligne={ligne}
                        onAnnuler={fermer}
                        onValider={async (saisie) => {
                          if (await agir(() => api.annulerReliquat(fiche.id, ligne.id, saisie))) {
                            fermer();
                          }
                        }}
                      />
                    ) : null}
                    {ouvert(ligne.id, "AILLEURS") ? (
                      <FormulaireCommandeAilleurs
                        ligne={ligne}
                        fournisseurActuelId={fiche.fournisseur.id}
                        avecPrix={voitLesMontants}
                        onAnnuler={fermer}
                        onValider={async (saisie) => {
                          // La réponse est la fiche de la commande où la ligne
                          // est désormais commandée : on y va.
                          const creee = { id: "" };
                          const faite = await agir(async () => {
                            const nouvelle = await api.commanderAilleurs(
                              fiche.id,
                              ligne.id,
                              saisie,
                            );
                            creee.id = nouvelle.id;
                            return nouvelle;
                          });
                          if (faite) {
                            fermer();
                            navigate(`/commandes-fournisseurs/${creee.id}`);
                          }
                        }}
                      />
                    ) : null}
                  </li>
                ),
              )}
          </ul>
        </section>
      ))}
      {fiche.autres_commandes_clients.length > 0 ? (
        <ul className="divide-y divide-filet border-t border-filet">
          {fiche.autres_commandes_clients.map((autre, rang) => (
            <li key={rang} className="px-4 py-3 text-sm text-encre-douce">
              Une commande client que vous ne voyez pas : {autre.nombre_lignes} ligne
              {autre.nombre_lignes > 1 ? "s" : ""}
            </li>
          ))}
        </ul>
      ) : null}
      {peutAjouter && premiere ? (
        <CorpsCarte className="border-t border-filet">
          <h3 className="mb-3 text-sm font-medium text-encre">Ajouter une ligne</h3>
          {fiche.ajout_ligne === "MOTIVE" ? (
            <p className="mb-3 text-sm text-encre-douce">
              La commande est envoyée : la ligne s'ajoute avec son motif, et peut désigner celle
              qu'elle remplace.
            </p>
          ) : null}
          <FormulaireLigne
            commandes={commandes}
            commandeParDefaut={premiere.id}
            avecPrix={voitLesMontants}
            remplacables={fiche.ajout_ligne === "MOTIVE" ? fiche.lignes : undefined}
            libelleValider="Ajouter la ligne"
            onValider={(champs) => agir(() => api.ajouterLigneFournisseur(fiche.id, champs))}
          />
        </CorpsCarte>
      ) : null}
    </Carte>
  );
}

type GesteOuvert = "CORRECTION" | "RELIQUAT" | "AILLEURS";

function quantite(ligne: LigneCommandeFournisseur) {
  return `${quantiteLisible(ligne.quantite_commandee)} ${ligne.unite}`;
}

function chez(liee: LigneLiee) {
  return (
    <Link
      to={`/commandes-fournisseurs/${liee.commande_fournisseur_id}`}
      className="underline underline-offset-4"
    >
      « {liee.designation} » chez {liee.fournisseur}
    </Link>
  );
}

/**
 * Ce qu'une ligne a vécu : envoyée, ses quantités en toutes lettres ; et, dès la
 * préparation quand elle vient d'ailleurs, ce qu'elle remplace et pourquoi.
 */
function HistoireDeLaLigne({
  ligne,
  envoyee,
}: {
  ligne: LigneDeLaFicheFournisseur;
  envoyee: boolean;
}) {
  if (!envoyee && !ligne.remplace && !ligne.motif_ajout && ligne.remplacee_par.length === 0) {
    return null;
  }
  return (
    <div className="flex flex-col gap-1 text-sm text-encre-douce">
      {envoyee ? <p className="text-encre">{quantitesEnLettres(ligne)}.</p> : null}
      {ligne.motif_ajout ? <p>Motif : {ligne.motif_ajout}.</p> : null}
      {ligne.remplace ? (
        ligne.remplacement ? (
          <EncartRemplacement remplace={ligne.remplace} remplacement={ligne.remplacement} />
        ) : (
          <p>Remplace {chez(ligne.remplace)}.</p>
        )
      ) : null}
      {ligne.remplacee_par.map((liee) => (
        <p key={liee.id}>Remplacée par {chez(liee)}.</p>
      ))}
      {ligne.annulations.map((annulation, rang) => (
        <p key={rang}>
          Annulé {quantiteLisible(annulation.quantite)} {ligne.unite} le{" "}
          {horodatage(annulation.annule_le)} : {annulation.motif}.
        </p>
      ))}
    </div>
  );
}

/** Un écart en toutes lettres : la couleur ne porte pas le sens (ADR-0024). */
function sens(ecart: number) {
  if (ecart > 0) return `hausse de ${euros(ecart)}`;
  if (ecart < 0) return `baisse de ${euros(-ecart)}`;
  return "aucun écart";
}

/**
 * Ticket #47 (décision 43) : sur la ligne qui en remplace une autre, les deux
 * prix, l'écart par unité et sur ce qu'elle commande, et la marge
 * prévisionnelle sur achats de sa commande client au prix remplacé puis au
 * prix nouveau. Rien ne se bloque : l'encart informe.
 */
function EncartRemplacement({
  remplace,
  remplacement,
}: {
  remplace: LigneLiee;
  remplacement: Remplacement;
}) {
  return (
    <section
      aria-label={`Remplace ${remplace.reference ?? remplace.designation}`}
      className="flex flex-col gap-2 rounded-md border border-filet p-3"
    >
      <p className="font-medium text-encre">
        Remplace {remplace.reference ? `la réf. ${remplace.reference}, ` : null}
        {chez(remplace)}
      </p>
      <dl className="grid grid-cols-2 gap-x-4 gap-y-3 sm:grid-cols-4">
        <Donnee libelle="Prix remplacé">
          <span className="tabular-nums">
            {remplacement.prix_remplace_ht === null
              ? "Sans prix"
              : euros(remplacement.prix_remplace_ht)}
          </span>
        </Donnee>
        <Donnee libelle="Nouveau prix">
          <span className="tabular-nums">
            {remplacement.prix_ht === null ? "Sans prix" : euros(remplacement.prix_ht)}
          </span>
        </Donnee>
        <Donnee libelle="Écart par unité">
          <span className="tabular-nums">
            {remplacement.ecart_ht === null ? "Non mesurable" : sens(remplacement.ecart_ht)}
          </span>
        </Donnee>
        <Donnee libelle="Sur la ligne">
          <span className="tabular-nums">
            {remplacement.incidence_ht === null ? "Non mesurable" : sens(remplacement.incidence_ht)}
          </span>
        </Donnee>
      </dl>
      <p className="tabular-nums text-encre">
        Marge prévisionnelle sur achats : au prix remplacé{" "}
        {remplacement.marge_avant_ht === null
          ? "inconnue (sans montant de vente, ou un prix manque)"
          : euros(remplacement.marge_avant_ht)}
        , au nouveau prix{" "}
        {remplacement.marge_apres_ht === null ? "inconnue" : euros(remplacement.marge_apres_ht)}
        {remplacement.marge_incomplete ? " (marge incomplète : le coût ne compte pas tout)" : null}
      </p>
    </section>
  );
}

const NE_REMPLACE_RIEN = "";

function FormulaireLigne({
  ligne,
  commandes,
  commandeParDefaut = "",
  avecPrix,
  remplacables,
  libelleValider,
  onAnnuler,
  onValider,
}: {
  ligne?: LigneCommandeFournisseur;
  commandes: CommandeClientProposee[];
  commandeParDefaut?: string;
  avecPrix: boolean;
  /** Après l'envoi : le motif s'exige, et la ligne peut en remplacer une de celles-ci. */
  remplacables?: LigneCommandeFournisseur[];
  libelleValider: string;
  onAnnuler?: () => void;
  onValider: (champs: Record<string, unknown>) => Promise<boolean>;
}) {
  const [valeurs, setValeurs] = useState(() => valeursDeLigne(commandeParDefaut, ligne));
  const [motif, setMotif] = useState("");
  const [remplace, setRemplace] = useState(NE_REMPLACE_RIEN);
  const [envoi, setEnvoi] = useState(false);
  const motive = remplacables !== undefined;

  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        const champs = champsDeLigne(valeurs, avecPrix);
        if (motive) {
          champs.motif = motif.trim();
          champs.remplace_ligne_id = remplace || null;
        }
        const faite = await onValider(champs);
        if (faite && !ligne) {
          setValeurs(valeursDeLigne(valeurs.commande_client_id));
          setMotif("");
          setRemplace(NE_REMPLACE_RIEN);
        }
        setEnvoi(false);
      }}
    >
      <ChampsLigne
        valeurs={valeurs}
        onChange={setValeurs}
        commandes={commandes}
        avecPrix={avecPrix}
      />
      {motive ? (
        <div className="grid gap-3 sm:grid-cols-2">
          <ChampMotif valeur={motif} onChange={setMotif} />
          <ChampSelection
            libelle="Remplace"
            options={[
              { valeur: NE_REMPLACE_RIEN, libelle: "Aucune ligne" },
              ...remplacables.map((l) => ({ valeur: l.id, libelle: l.designation })),
            ]}
            value={remplace}
            onChange={(e) => setRemplace(e.target.value)}
          />
        </div>
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || (motive && !motif.trim())}>
          {libelleValider}
        </Bouton>
        {onAnnuler ? (
          <Bouton type="button" variante="discret" onClick={onAnnuler}>
            Annuler
          </Bouton>
        ) : null}
      </div>
    </form>
  );
}

/**
 * Les problèmes fournisseur (ticket #16) : ils vivent à côté de l'avancement,
 * sans le changer (ADR-0003). Un problème sur une ligne pèse sur son lot, et sa
 * timeline reste en alerte tant qu'il est ouvert. Il ne se supprime pas : il se
 * résout, en disant comment, et la base le fige ensuite.
 *
 * Une non-conformité constatée à la réception se tranche par une décision
 * (ticket #23) : acceptée en l'état, acceptée avec remise — la base crée alors
 * la demande d'avoir —, ou maintenue ; un surplus peut se solder par un avoir.
 */
function Problemes({
  fiche,
  peutAgir,
  voitLesMontants,
  agir,
  proposition,
  onPropositionTraitee,
}: {
  fiche: FicheCommandeFournisseur;
  peutAgir: boolean;
  voitLesMontants: boolean;
  agir: Agir;
  proposition: ProblemePropose | null;
  onPropositionTraitee: () => void;
}) {
  // ADR-0049 : décider d'une non-conformité demande sa permission, en plus d'agir.
  const peutTrancher = useSession().peut("TRAITER_NON_CONFORMITES_FOURNISSEURS");
  const [ouvert, setOuvert] = useState(false);
  const [enResolution, setEnResolution] = useState<string | null>(null);
  const [enReouverture, setEnReouverture] = useState<string | null>(null);
  const ouverture = ouvert || proposition !== null;
  const fermer = () => {
    setOuvert(false);
    onPropositionTraitee();
  };

  return (
    <Carte id="problemes-fournisseur">
      <EnTeteCarte className="flex items-center justify-between gap-3">
        <h2 className="text-sm font-semibold text-encre">Problèmes fournisseur</h2>
        {peutAgir && !ouverture ? (
          <Bouton taille="compact" variante="discret" onClick={() => setOuvert(true)}>
            <AlertTriangle className="h-3.5 w-3.5" aria-hidden />
            Signaler un problème
          </Bouton>
        ) : null}
      </EnTeteCarte>
      {ouverture ? (
        <CorpsCarte className="border-b border-filet">
          <FormulaireProbleme
            // Une autre proposition repart d'un formulaire neuf.
            key={proposition ? `${proposition.ligne_commande_fournisseur_id}-${proposition.description}` : "vide"}
            fiche={fiche}
            initial={proposition}
            onAnnuler={fermer}
            onValider={async (saisie) => {
              if (await agir(() => api.ouvrirProblemeFournisseur(fiche.id, saisie))) {
                fermer();
              }
            }}
          />
        </CorpsCarte>
      ) : null}
      {fiche.problemes.length === 0 ? (
        <Vide
          titre="Aucun problème signalé"
          detail="Un retard, une casse, une erreur de référence : signalez-le ici pour le suivre."
        />
      ) : (
        <ul className="divide-y divide-filet">
          {fiche.problemes.map((probleme) => (
            <li key={probleme.id} className="flex flex-col gap-2 px-4 py-3 text-sm">
              <div className="flex flex-wrap items-start justify-between gap-2">
                <p className="min-w-0 flex-1">
                  <span className="font-medium text-encre">
                    {typeProblemeFournisseur(probleme.type)}
                  </span>
                  <span className="text-encre-douce"> — {cible(probleme)}</span>
                </p>
                <Etiquette ton={probleme.resolu_le ? "succes" : "alerte"}>
                  {etatDuProbleme(probleme)}
                </Etiquette>
              </div>
              <p className="whitespace-pre-line text-encre">{probleme.description}</p>
              {probleme.non_conformite && !probleme.resolu_le && !probleme.decision ? (
                <p className="text-encre">
                  Non conforme : {quantiteLisible(probleme.quantite_non_conforme ?? 0)}{" "}
                  {probleme.unite}, à décider — tant qu'elle n'est pas acceptée, elle reste à
                  recevoir.
                </p>
              ) : null}
              <p className="text-xs text-encre-douce">Signalé le {horodatage(probleme.signale_le)}</p>
              {probleme.rouvert_le && !probleme.resolu_le && !enAttente(probleme) ? (
                <p className="text-encre-douce">{reouvertureLue(probleme)}</p>
              ) : null}
              {probleme.resolu_le || enAttente(probleme) ? (
                <>
                  <p className="flex items-start gap-1.5 text-encre-douce">
                    {enAttente(probleme) ? (
                      <Hourglass className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    ) : (
                      <CheckCircle2 className="mt-0.5 h-4 w-4 shrink-0" aria-hidden />
                    )}
                    <span>
                      {enAttente(probleme) ? maintenueLue(probleme) : resolutionLue(probleme)}
                    </span>
                  </p>
                  {/* Ticket #65 : qui peut le trancher peut le rouvrir ; la base dit
                      ce qui l'en empêche (une demande d'avoir à abandonner d'abord). */}
                  {peutAgir && (!probleme.non_conformite || peutTrancher) ? (
                    enReouverture === probleme.id ? (
                      <FormulaireReouverture
                        onAnnuler={() => setEnReouverture(null)}
                        onValider={async (motif) => {
                          const faite = await agir(() =>
                            api.rouvrirProbleme(fiche.id, probleme.id, motif),
                          );
                          if (faite) setEnReouverture(null);
                        }}
                      />
                    ) : (
                      <span>
                        <Bouton
                          taille="compact"
                          variante="discret"
                          aria-label={`Rouvrir le problème : ${typeProblemeFournisseur(
                            probleme.type,
                          )}, ${cible(probleme)}`}
                          onClick={() => setEnReouverture(probleme.id)}
                        >
                          <RotateCcw className="h-3.5 w-3.5" aria-hidden />
                          Rouvrir
                        </Bouton>
                      </span>
                    )
                  ) : null}
                </>
              ) : enResolution === probleme.id ? (
                probleme.non_conformite ? (
                  <FormulaireDecision
                    probleme={probleme}
                    voitLesMontants={voitLesMontants}
                    onAnnuler={() => setEnResolution(null)}
                    onValider={async (saisie) => {
                      const faite = await agir(() =>
                        api.deciderNonConformite(fiche.id, probleme.id, saisie),
                      );
                      if (faite) setEnResolution(null);
                    }}
                  />
                ) : (
                  <FormulaireResolution
                    avecAvoir={probleme.avoir_possible}
                    onAnnuler={() => setEnResolution(null)}
                    onValider={async (resolution, montant) => {
                      const faite = await agir(() =>
                        api.resoudreProblemeFournisseur(fiche.id, probleme.id, resolution, montant),
                      );
                      if (faite) setEnResolution(null);
                    }}
                  />
                )
              ) : peutAgir && (!probleme.non_conformite || peutTrancher) ? (
                <span>
                  {probleme.non_conformite ? (
                    <Bouton
                      taille="compact"
                      variante="discret"
                      aria-label={`Décider de la non-conformité : ${cible(probleme)}`}
                      onClick={() => setEnResolution(probleme.id)}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                      Décider
                    </Bouton>
                  ) : (
                    <Bouton
                      taille="compact"
                      variante="discret"
                      aria-label={`Résoudre le problème : ${typeProblemeFournisseur(
                        probleme.type,
                      )}`}
                      onClick={() => setEnResolution(probleme.id)}
                    >
                      <CheckCircle2 className="h-3.5 w-3.5" aria-hidden />
                      Résoudre
                    </Bouton>
                  )}
                </span>
              ) : null}
            </li>
          ))}
        </ul>
      )}
    </Carte>
  );
}

/** Ticket #69 : maintenue, la non-conformité reste ouverte, décidée mais pas close. */
function enAttente(probleme: ProblemeFournisseur) {
  return probleme.decision === "MAINTENUE" && !probleme.resolu_le;
}

function etatDuProbleme(probleme: ProblemeFournisseur) {
  if (enAttente(probleme)) return "Non-conformité maintenue, en attente";
  if (probleme.decision) return decisionNonConformite(probleme.decision);
  if (probleme.resolu_le) return "Résolu";
  if (probleme.rouvert_le) return probleme.non_conformite ? "Rouverte, à décider" : "Rouvert";
  return probleme.non_conformite ? "À décider" : "Ouvert";
}

/** Ticket #65 : la dernière réouverture, qui, quand, pourquoi. */
function reouvertureLue(probleme: ProblemeFournisseur) {
  const qui = probleme.rouvert_par ? `, par ${probleme.rouvert_par}` : "";
  const motif = probleme.motif_reouverture ? ` : ${probleme.motif_reouverture}` : "";
  return `Rouvert le ${horodatage(probleme.rouvert_le ?? "")}${qui}${motif}.`;
}

/** Ticket #69 : qui a maintenu, pourquoi, et ce que la base attend pour fermer. */
function maintenueLue(probleme: ProblemeFournisseur) {
  const qui = probleme.resolu_par ? ` par ${probleme.resolu_par}` : "";
  return (
    `Maintenue${qui} : ${probleme.resolution}. En attente de la marchandise : elle se ferme ` +
    "d'elle-même quand la quantité maintenue est reçue conforme, annulée ou commandée ailleurs."
  );
}

/** Qui a résolu, quand, comment ; la décision et l'avoir qu'elle a demandé. */
function resolutionLue(probleme: ProblemeFournisseur) {
  const quand = `le ${horodatage(probleme.resolu_le ?? "")}`;
  const qui = probleme.resolu_par ? `, par ${probleme.resolu_par}` : "";
  const avoir =
    probleme.montant_avoir_ht !== null
      ? ` Demande d'avoir de ${euros(probleme.montant_avoir_ht)} HT.`
      : "";
  const geste =
    probleme.decision === "MAINTENUE" ? "Maintenue, close" : probleme.decision ? "Décidé" : "Résolu";
  return `${geste} ${quand}${qui} : ${probleme.resolution}.${avoir}`;
}

function cible(probleme: ProblemeFournisseur) {
  if (!probleme.designation_ligne) return "toute la commande";
  return probleme.code_lot
    ? `${probleme.designation_ligne}, lot ${probleme.code_lot}`
    : probleme.designation_ligne;
}

const TOUTE_LA_COMMANDE = "";

function FormulaireProbleme({
  fiche,
  initial,
  onAnnuler,
  onValider,
}: {
  fiche: FicheCommandeFournisseur;
  initial: ProblemePropose | null;
  onAnnuler: () => void;
  onValider: (saisie: {
    type: TypeProblemeFournisseur;
    description: string;
    ligne_commande_fournisseur_id: string | null;
  }) => Promise<void>;
}) {
  const [type, setType] = useState<TypeProblemeFournisseur | "">(initial?.type ?? "");
  const [ligneId, setLigneId] = useState(
    initial?.ligne_commande_fournisseur_id ?? TOUTE_LA_COMMANDE,
  );
  const [description, setDescription] = useState(initial?.description ?? "");
  const [envoi, setEnvoi] = useState(false);
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        if (!type) return;
        setEnvoi(true);
        await onValider({
          type,
          description: description.trim(),
          ligne_commande_fournisseur_id: ligneId || null,
        });
        setEnvoi(false);
      }}
    >
      <div className="grid gap-3 sm:grid-cols-2">
        <ChampSelection
          libelle="Type de problème"
          required
          options={[
            { valeur: "", libelle: "Choisir un type" },
            ...Object.entries(TYPES_PROBLEME_FOURNISSEUR).map(([valeur, libelle]) => ({
              valeur,
              libelle,
            })),
          ]}
          value={type}
          onChange={(e) => setType(e.target.value as TypeProblemeFournisseur | "")}
        />
        <ChampSelection
          libelle="Ce qu'il touche"
          options={[
            { valeur: TOUTE_LA_COMMANDE, libelle: "Toute la commande" },
            ...fiche.lignes.map((ligne) => ({
              valeur: ligne.id,
              libelle: ligne.code_lot
                ? `${ligne.designation} — lot ${ligne.code_lot}`
                : ligne.designation,
            })),
          ]}
          value={ligneId}
          onChange={(e) => setLigneId(e.target.value)}
        />
      </div>
      <ChampTexte
        libelle="Description"
        required
        value={description}
        onChange={(e) => setDescription(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !type || !description.trim()}>
          Signaler le problème
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

/** Un montant saisi, virgule ou point ; `null` s'il est vide ou illisible. */
function montantLu(saisi: string) {
  const lu = Number(saisi.replace(",", ".").trim());
  return saisi.trim() && Number.isFinite(lu) && lu > 0 ? lu : null;
}

function FormulaireResolution({
  avecAvoir,
  onAnnuler,
  onValider,
}: {
  /** Ticket #23 : un surplus se solde par un avoir, que la base demande. */
  avecAvoir: boolean;
  onAnnuler: () => void;
  onValider: (resolution: string, montantAvoirHt: number | null) => Promise<void>;
}) {
  const [resolution, setResolution] = useState("");
  const [montant, setMontant] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const montantIllisible = montant.trim() !== "" && montantLu(montant) === null;
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        await onValider(resolution.trim(), montantLu(montant));
        setEnvoi(false);
      }}
    >
      <ChampTexte
        libelle="Comment il a été résolu"
        required
        value={resolution}
        onChange={(e) => setResolution(e.target.value)}
      />
      {avecAvoir ? (
        <Champ
          libelle="Avoir à demander au fournisseur, HT (€), facultatif"
          inputMode="decimal"
          value={montant}
          onChange={(e) => setMontant(e.target.value)}
        />
      ) : null}
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !resolution.trim() || montantIllisible}>
          Enregistrer la résolution
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

/**
 * Ticket #65 (décision 60) : rouvrir un problème résolu. Le motif est celui de
 * cette réouverture ; la décision passée reste au journal.
 */
function FormulaireReouverture({
  onAnnuler,
  onValider,
}: {
  onAnnuler: () => void;
  onValider: (motif: string) => Promise<void>;
}) {
  const [motif, setMotif] = useState("");
  const [envoi, setEnvoi] = useState(false);
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        setEnvoi(true);
        await onValider(motif.trim());
        setEnvoi(false);
      }}
    >
      <ChampTexte
        libelle="Motif de la réouverture"
        required
        value={motif}
        onChange={(e) => setMotif(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !motif.trim()}>
          Rouvrir le problème
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

/**
 * Ticket #23 : trancher une non-conformité. La remise dit son montant, que seul
 * qui voit les montants décide ; la décision ne se reprend pas.
 */
function FormulaireDecision({
  probleme,
  voitLesMontants,
  onAnnuler,
  onValider,
}: {
  probleme: ProblemeFournisseur;
  voitLesMontants: boolean;
  onAnnuler: () => void;
  onValider: (saisie: {
    decision: DecisionNonConformite;
    motif: string;
    montant_remise_ht: number | null;
  }) => Promise<void>;
}) {
  const [decision, setDecision] = useState<DecisionNonConformite | "">("");
  const [motif, setMotif] = useState("");
  const [montant, setMontant] = useState("");
  const [envoi, setEnvoi] = useState(false);
  const remise = decision === "ACCEPTEE_AVEC_REMISE";
  const constate =
    `${quantiteLisible(probleme.quantite_non_conforme ?? 0)} ` + `${probleme.unite ?? ""}`;
  const complet =
    decision !== "" && motif.trim() !== "" && (!remise || montantLu(montant) !== null);
  return (
    <form
      className="flex flex-col gap-3"
      onSubmit={async (evenement) => {
        evenement.preventDefault();
        if (!decision) return;
        setEnvoi(true);
        await onValider({
          decision,
          motif: motif.trim(),
          montant_remise_ht: remise ? montantLu(montant) : null,
        });
        setEnvoi(false);
      }}
    >
      <ChampPuces
        libelle={`Décision sur ${constate} non conforme`}
        required
        options={Object.entries(DECISIONS_NON_CONFORMITE)
          .filter(([valeur]) => voitLesMontants || valeur !== "ACCEPTEE_AVEC_REMISE")
          .map(([valeur, libelle]) => ({ valeur, libelle }))}
        valeur={decision}
        onChanger={(valeur) => setDecision(valeur as DecisionNonConformite)}
      />
      <p className="text-xs text-encre-douce">
        Acceptée, elle compte comme reçue et le problème se ferme ; maintenue, elle reste à
        recevoir et le problème reste ouvert, sans rien bloquer : il se ferme quand la
        quantité est reçue conforme, annulée ou commandée ailleurs. Pour changer d'avis,
        on rouvre le problème.
      </p>
      {remise ? (
        <Champ
          libelle="Montant de la remise, HT (€)"
          required
          inputMode="decimal"
          value={montant}
          onChange={(e) => setMontant(e.target.value)}
        />
      ) : null}
      <ChampTexte
        libelle="Motif"
        required
        value={motif}
        onChange={(e) => setMotif(e.target.value)}
      />
      <div className="flex flex-wrap gap-2">
        <Bouton type="submit" disabled={envoi || !complet}>
          Enregistrer la décision
        </Bouton>
        <Bouton type="button" variante="discret" onClick={onAnnuler}>
          Annuler
        </Bouton>
      </div>
    </form>
  );
}

/**
 * Ticket #39 (story 51) : le prix commandé, et le prix retenu à côté quand un
 * écart de prix accepté l'a remplacé.
 */
function prixDeLaLigne(ligne: LigneCommandeFournisseur) {
  if (ligne.prix_unitaire_ht === null && ligne.prix_retenu_ht === null) return "Sans prix";
  const commande =
    ligne.prix_unitaire_ht === null ? "sans prix" : `${euros(ligne.prix_unitaire_ht)} l'unité`;
  if (ligne.prix_retenu_ht === null) return commande;
  return `Commandé ${commande}, retenu ${euros(ligne.prix_retenu_ht)}`;
}
